import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowUpRight, Flame, X, Info, Globe2, KeyRound, Wrench, Factory, Activity, Sparkles } from "lucide-react";
import Reveal from "../Reveal";
import { getActiveLang } from "../../lib/activeLang";
import { t } from "../../lib/ui";
import { VESTA_LAUNCH_AT, useVestaLaunched, withAi } from "../../lib/vestaLaunch";

export const COPPER = "#C8875A";
const COPPER_GRAD = "linear-gradient(135deg,#F3C9A1 0%,#C8875A 45%,#8A5231 100%)";
const VESTA_TITLE_VIDEO = "/tech/ai-vesta-logo-card.mp4";
const VESTA_TITLE_POSTER = "/tech/ai-vesta-logo-card-poster.jpg";
const VESTA_DEMO = "https://rete.torri.solar/vesta-demo";
const SG = { fontFamily: "Space Grotesk" };
const IN = { fontFamily: "Inter" };
const AR = { fontFamily: "Arial, Arimo, 'Liberation Sans', sans-serif" };

const FEATURES = [
  { Icon: Globe2, t: "Vesta World", d: "Il social network privato della rete Torri: post, messaggi, profili e lavori realizzati dei partner." },
  { Icon: KeyRound, t: "Chiavi in mano", d: "Configuratori e strumenti per offrire al cliente finale un'esperienza completa, dal preventivo alla proposta." },
  { Icon: Wrench, t: "Tools tecnici", d: "Calcoli, schede e strumenti per studiare e preparare il cantiere." },
  { Icon: Factory, t: "Backoffice con la fabbrica", d: "Listino riservato, commesse, preventivi e magazzino in collegamento diretto con Torri." },
  { Icon: Activity, t: "TORRI Monitoring con Vesta", d: "Gli impianti dei tuoi clienti in tempo reale nella Vista Cliente: energia, batteria, lettura automatica della bolletta e risparmio, report periodici e l'assistente Vesta a voce e per iscritto. Versione Pro per le aziende." },
  { Icon: Sparkles, t: "My Vesta", d: "Il tuo profilo, i Punti Vesta, il Catalogo punti e la tua pagina su torri.solar." },
];

function ModalShell({ onClose, title, testId, children }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    document.body.classList.add("tech-modal-open");
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      document.body.classList.remove("tech-modal-open");
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[80] flex items-start md:items-center justify-center p-0 md:p-6" data-testid={testId}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} data-testid={`${testId}-overlay`} />
      <div className="relative w-full md:max-w-5xl h-full md:h-auto md:max-h-[94vh] flex flex-col bg-[#0a0a0a] text-white border md:rounded-lg shadow-2xl overflow-hidden" style={{ borderColor: "rgba(200,135,90,0.35)" }}>
        <div className="shrink-0 min-h-14 py-1.5 flex items-center justify-between gap-3 pl-6 md:pl-8 pr-2 border-b border-white/10 bg-black">
          <span className="text-[11px] md:text-[12px] uppercase tracking-[0.12em] md:tracking-[0.2em] leading-snug font-semibold" style={{ ...SG, color: COPPER }} data-testid={`${testId}-title`}>{title}</span>
          <button onClick={onClose} aria-label="Chiudi" data-testid={`${testId}-close`} className="h-11 w-11 shrink-0 flex items-center justify-center rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors">
            <X size={22} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid={`${testId}-scroll`}>{children}</div>
      </div>
    </div>
  );
}

export function VestaInfoModal({ onClose }) {
  return (
    <ModalShell onClose={onClose} title="Informazioni" testId="vesta-info">
        <div className="px-6 md:px-14 py-10 md:py-12">
          <h2 className="text-center font-black text-3xl md:text-5xl tracking-tight" style={{ fontFamily: "Archivo", color: COPPER }}>Cos'è Ai VESTA</h2>
          <p className="mt-6 text-white/85 text-base md:text-xl leading-relaxed" style={IN}>Ai VESTA è il motore di Ai sviluppato dall'azienda e riservato ai partner installatori Torri. In un unico posto trovi un sistema digitale di strumenti, dati e network per promuovere l'alta gamma e crescere insieme alla rete.</p>

          <div className="mt-8 rounded-2xl border p-5 md:p-7" style={{ borderColor: "rgba(200,135,90,0.35)", background: "rgba(200,135,90,0.06)" }} data-testid="vesta-modal-why">
            <p className="text-sm md:text-base font-bold uppercase tracking-[0.14em]" style={{ ...SG, color: COPPER }}>Perché “Vesta”</p>
            <p className="mt-3 text-white/80 text-sm md:text-lg leading-relaxed" style={IN}>Il nome Vesta richiama una delle radici più antiche della cultura italiana: il fuoco custodito, simbolo di continuità, appartenenza e comunità. Un significato che abbiamo scelto per rappresentare una rete di professionisti e imprese che custodiscono il valore della tecnologia e del saper fare italiano.</p>
          </div>

          <ul className="mt-10 space-y-6" data-testid="vesta-modal-features">
            {FEATURES.map(({ Icon, t, d }) => (
              <li key={t} className="flex gap-4">
                <Icon size={28} strokeWidth={1.6} className="shrink-0 mt-0.5" style={{ color: COPPER }} />
                <div>
                  <p className="font-bold text-lg md:text-2xl" style={SG}>{t}</p>
                  <p className="mt-1 text-white/65 text-sm md:text-lg leading-relaxed" style={IN}>{d}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
    </ModalShell>
  );
}

function useDemoHeight() {
  const [h, setH] = useState(760);
  useEffect(() => {
    const onMsg = (e) => {
      if (e.origin !== new URL(VESTA_DEMO).origin || e.data?.type !== "vesta-demo-height") return;
      const n = Number(e.data.height);
      if (n > 0) setH(Math.max(760, Math.ceil(n)));
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);
  return h;
}

function VestaModal({ onClose }) {
  const demoHeight = useDemoHeight();
  return (
    <ModalShell onClose={onClose} title="Una delle radici più antiche della cultura italiana: il fuoco custodito" testId="vesta-modal">
      <div className="bg-black pt-8 md:pt-10" data-testid="vesta-modal-logo">
        <AutoVideo src="/tech/ai-vesta-logo-wide.mp4?v=3" poster="/tech/ai-vesta-logo-wide-poster.jpg?v=3" className="block w-full md:w-[60%] mx-auto aspect-[1440/640] object-cover" testId="vesta-modal-video" />
      </div>
      <iframe src={getActiveLang() === "it" ? VESTA_DEMO : `${VESTA_DEMO}?lang=en`} title="Demo Ai VESTA" allow="microphone; autoplay" loading="lazy" style={{ width: "100%", height: demoHeight, border: 0, display: "block" }} data-testid="vesta-modal-iframe" />
    </ModalShell>
  );
}

export function AutoVideo({ src, poster, className, testId, pauseMs = 0 }) {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    v.muted = true;
    v.defaultMuted = true;
    let timer;
    let visible = false;
    const play = () => v.play().catch(() => {});
    const onEnded = () => { timer = setTimeout(() => { v.currentTime = 0; if (visible) play(); }, pauseMs); };
    if (pauseMs) v.addEventListener("ended", onEnded);
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; if (!visible) v.pause(); else if (!v.ended) play(); }, { rootMargin: "200px" });
    io.observe(v);
    document.addEventListener("touchstart", play, { once: true, passive: true });
    document.addEventListener("scroll", play, { once: true, passive: true });
    return () => { clearTimeout(timer); v.removeEventListener("ended", onEnded); io.disconnect(); document.removeEventListener("touchstart", play); document.removeEventListener("scroll", play); };
  }, [pauseMs]);
  return <video ref={ref} src={src} poster={poster} muted loop={!pauseMs} playsInline preload="auto" aria-hidden="true" className={className} data-testid={testId} />;
}

export function VestaWordmark({ className = "", style, testId = "vesta-wordmark" }) {
  return (
    <span className={`block ${className}`} style={style}>
      <span className="sr-only">Ai VESTA</span>
      <AutoVideo src={VESTA_TITLE_VIDEO} poster={VESTA_TITLE_POSTER} pauseMs={10000} className="block w-full aspect-[1180/532] object-cover" testId={testId} />
    </span>
  );
}

function LaunchCountdown() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const left = Math.max(0, Math.floor((VESTA_LAUNCH_AT - now) / 1000));
  const labels = t("vesta_cd").split("|");
  const parts = [Math.floor(left / 86400), Math.floor(left / 3600) % 24, Math.floor(left / 60) % 60, left % 60];
  return (
    <div className="pt-6 border-t" style={{ borderColor: "rgba(200,135,90,0.25)" }} data-testid="vesta-countdown">
      <p className="text-[11px] md:text-xs uppercase tracking-[0.2em] text-white/55" style={AR} data-testid="vesta-launch-date">{t("vesta_launch")}</p>
      {left > 0 && (
        <div className="mt-4 flex items-start" aria-live="off">
          {parts.map((v, i) => (
            <React.Fragment key={labels[i]}>
              {i > 0 && <span className="vesta-cd-colon px-1.5 md:px-2.5 text-3xl md:text-5xl font-bold leading-none" aria-hidden="true">:</span>}
              <div className="text-center">
                <div className="vesta-cd-digit text-4xl md:text-6xl font-bold leading-none tabular-nums" data-testid={`vesta-countdown-${i}`}>{String(v).padStart(2, "0")}</div>
                <div className="mt-2 text-[10px] md:text-[11px] uppercase tracking-[0.24em] text-white/45" style={AR}>{labels[i]}</div>
              </div>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

function VestaTechCard({ large, live, onOpen, onInfo }) {
  const clickable = live ? { role: "button", tabIndex: 0, onClick: onOpen, onKeyDown: (e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onOpen()) } : {};
  return (
    <div {...clickable} className={`group relative block w-full text-left bg-black border border-white/10 transition-colors ${live ? "cursor-pointer hover:border-[rgba(200,135,90,0.45)]" : ""}`} style={{ boxShadow: `inset 0 3px 0 0 ${COPPER}` }} data-testid="tech-card-tech-ai-vesta" id="tech-ai-vesta">
      <span className="absolute top-0 left-5 md:left-6 -translate-y-1/2 z-10 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.1em] md:tracking-[0.16em] font-bold px-2.5 py-1 leading-none whitespace-nowrap" style={{ ...SG, background: COPPER_GRAD, color: "#2a1608" }} data-testid="tech-badge-tech-ai-vesta">
        <span>{withAi(t("vesta_badge"))}</span>
      </span>
      <div className={`grid md:grid-cols-2 gap-x-12 ${large ? "p-10 lg:p-14" : "p-8"}`}>
        <div>
          <div className="flex items-center gap-2">
            <Flame size={large ? 24 : 20} strokeWidth={1.6} style={{ color: COPPER }} />
            <span className="text-[11px] uppercase tracking-[0.08em] md:tracking-[0.16em] text-white/60" style={SG}>{t("vesta_caption")}</span>
          </div>
          <h3 className="relative -ml-6 w-[calc(100%+3rem)] max-w-[480px]" data-testid="vesta-logo-title">
            <span className="sr-only">Ai VESTA</span>
            <AutoVideo src={VESTA_TITLE_VIDEO} poster={VESTA_TITLE_POSTER} className="block w-full aspect-[1180/532] object-cover" testId="tech-card-video-tech-ai-vesta" />
          </h3>
          <p className={`font-semibold ${large ? "text-base lg:text-lg" : "text-sm"}`} style={{ ...SG, color: COPPER }}>{t("vesta_claim")}</p>
        </div>
        <div className="flex flex-col md:justify-end">
          <p className={`text-white/60 leading-relaxed max-w-[52ch] ${large ? "mt-5 md:mt-0 mb-8 text-base" : "mt-4 md:mt-0 mb-6 text-sm"}`} style={IN}>{t("vesta_desc")}</p>
          {live ? (
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-white/60 group-hover:text-white transition-colors">
                <span className="text-xs md:text-sm uppercase tracking-[0.05em] font-bold whitespace-nowrap" style={{ fontFamily: "Arial, sans-serif" }}>{t("vesta_cta")}</span>
                <ArrowUpRight className="shrink-0 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" size={large ? 22 : 18} />
              </div>
              <button type="button" onClick={(e) => { e.stopPropagation(); onInfo(); }} onKeyDown={(e) => e.stopPropagation()} aria-label="Informazioni su Ai VESTA" title="Informazioni" data-testid="vesta-info-open" className="relative h-8 w-8 shrink-0 flex items-center justify-center rounded-full border transition-colors after:absolute after:-inset-1.5 after:content-[''] hover:bg-[rgba(200,135,90,0.18)]" style={{ color: COPPER, borderColor: "rgba(200,135,90,0.6)" }}>
                <Info size={16} />
              </button>
            </div>
          ) : (
            <LaunchCountdown />
          )}
        </div>
      </div>
    </div>
  );
}

export function VestaTechBlock({ large = false, live: forceLive = false }) {
  const launched = useVestaLaunched();
  const live = forceLive || launched;
  const [open, setOpen] = useState(null);
  const close = useCallback(() => setOpen(null), []);
  const location = useLocation();
  useEffect(() => {
    if (live && location.hash === "#tech-ai-vesta") setOpen("demo");
  }, [live, location.hash, location.key]);
  return (
    <div className={large ? "mt-8 lg:mt-[30px]" : "mt-10 lg:mt-[30px]"} data-testid="vesta-tech-block">
      <Reveal><VestaTechCard large={large} live={live} onOpen={() => setOpen("demo")} onInfo={() => setOpen("info")} /></Reveal>
      {open === "demo" && <VestaModal onClose={close} />}
      {open === "info" && <VestaInfoModal onClose={close} />}
    </div>
  );
}
